import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import BiometricLogin from '../components/BiometricLogin';
import BiometricService from '../services/BiometricService';
import DeviceUserService from '../services/DeviceUserService';
import { config, getApiBaseUrl } from '../config';
import {
  hasRecentPreviousSessionTimeout,
  peekLoginNotice,
  type LoginNoticeKind,
} from '../utils/sessionFlags';
import axios from 'axios';

// Servicio API mínimo para login
class LoginApiService {
  private static baseURL = getApiBaseUrl();

  static setToken(token: string) {
    // Guardar token en localStorage para que esté disponible para todas las peticiones
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  static async login(email: string, password: string) {
    try {
      const response = await axios.post(`${this.baseURL}/login`, {
        email,
        password,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }
}

// Componente de Login (extraído de App.tsx para code splitting)
const LoginPage: React.FC<{
  onLogin: (token: string, email: string, userData: any) => void;
}> = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(() => {
    const lastEmail = localStorage.getItem('lastEmail');
    return lastEmail || config.DEFAULT_EMAIL || '';
  });
  const [password, setPassword] = useState(config.DEFAULT_PASSWORD || '');
  const [loading, setLoading] = useState(false);
  const [, setHasBiometric] = useState<boolean | null>(null);
  const [isDeviceUser, setIsDeviceUser] = useState<boolean>(false);
  const [checkingUserType, setCheckingUserType] = useState<boolean>(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSessionTimeoutNotice, setShowSessionTimeoutNotice] = useState(
    () => hasRecentPreviousSessionTimeout()
  );
  const [loginNotice, setLoginNotice] = useState<LoginNoticeKind | null>(() => peekLoginNotice());

  const handleBiometricSuccess = useCallback(
    (authToken: string, userData?: any) => {
      LoginApiService.setToken(authToken);
      const emailLower = email.toLowerCase();
      localStorage.setItem('lastEmail', emailLower);
      const finalUserData = userData || { email: emailLower };
      onLogin(authToken, emailLower, finalUserData);
    },
    [email, onLogin]
  );

  const handleBiometricError = useCallback((_err: string) => {
    // silencioso
  }, []);

  // Función para verificar el tipo de usuario
  const checkUserType = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || !emailToCheck.trim()) {
      setHasBiometric(null);
      setIsDeviceUser(false);
      setCheckingUserType(false);
      return;
    }

    // No verificar si el email no parece completo (menos de 3 caracteres o no tiene @)
    const emailLower = emailToCheck.toLowerCase().trim();
    if (emailLower.length < 3 || (!emailLower.includes('@') && emailLower.length < 5)) {
      setHasBiometric(null);
      setIsDeviceUser(false);
      setCheckingUserType(false);
      return;
    }

    setCheckingUserType(true);
    try {
      console.log('[LoginPage] Verificando tipo de usuario para:', emailToCheck);
      const biometricService = BiometricService.getInstance();
      const deviceUserService = DeviceUserService.getInstance();
      
      // Verificar ambos en paralelo
      const [hasBiometricCredential, deviceCheck] = await Promise.all([
        biometricService.hasBiometricCredentials(emailLower),
        deviceUserService.isDeviceUser(emailLower)
      ]);
      
      console.log('[LoginPage] Resultados - Biometric:', hasBiometricCredential, 'Device:', deviceCheck.isDevice);
      
      setHasBiometric(hasBiometricCredential);
      setIsDeviceUser(deviceCheck.isDevice);
      
      // Si es dispositivo, limpiar el password
      if (deviceCheck.isDevice) {
        console.log('[LoginPage] Usuario es dispositivo, limpiando password');
        setPassword('');
      }
    } catch (error) {
      console.error('[LoginPage] Error verificando tipo de usuario:', error);
      setHasBiometric(false);
      setIsDeviceUser(false);
    } finally {
      setCheckingUserType(false);
    }
  }, []);

  // Verificar cuando cambia el email (con debounce para evitar muchas peticiones)
  useEffect(() => {
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    const timer = setTimeout(() => {
      checkUserType(email);
    }, 500);

    return () => clearTimeout(timer);
  }, [email, checkUserType]);

  // Verificar también al montar el componente si hay email cargado desde localStorage
  useEffect(() => {
    // Delay para asegurar que el componente esté completamente montado y los servicios estén listos
    const timer = setTimeout(() => {
      if (email && email.trim() && !checkingUserType) {
        console.log('[LoginPage] Verificando tipo de usuario al montar componente para:', email);
        checkUserType(email);
      }
    }, 300); // Aumentado a 300ms para dar más tiempo

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // Handler para cambiar idioma
  const handleLanguageChange = (lang: 'es' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    setLanguageMenuAnchor(null);
  };

  const handleLogin = async () => {
    // Validar que el email esté presente
    if (!email) {
      setError(t('login.fillAllFields') || 'Por favor completa todos los campos');
      return;
    }
    
    // Para usuarios no dispositivo, validar que password esté presente
    if (!isDeviceUser && !password) {
      setError(t('login.fillAllFields') || 'Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const emailLower = email.toLowerCase();
      localStorage.setItem('lastEmail', emailLower);
      
      // Para usuarios dispositivo, intentar primero sin password
      // Si falla, el backend indicará que se requiere password
      const loginPassword = (isDeviceUser && !password) ? '' : password;
      const response = await LoginApiService.login(emailLower, loginPassword);
      
      // Guardar token en localStorage y configurar axios INMEDIATAMENTE
      // setToken ya guarda en localStorage, así que no hace falta duplicar
      LoginApiService.setToken(response.token);

      // Llamar a onLogin que también guardará el token y usuario
      onLogin(response.token, emailLower, response.user);
    } catch (error: any) {
      let errorMessage = t('login.error') || 'Error al iniciar sesión';
      if (error.response?.status === 403 && error.response?.data?.disabled === true) {
        errorMessage = t('login.userDisabled') || 'Usuario deshabilitado';
      } else if (error.response?.data?.requiresPassword && error.response?.data?.deviceUser) {
        // Usuario dispositivo que requiere password porque la IP no coincide
        // Usar traducción según el idioma actual
        const errorMsg = i18n.language === 'en' 
          ? (error.response.data.error_en || t('login.deviceUserPasswordRequired') || 'Data does not match. Please enter your password.')
          : (error.response.data.error || t('login.deviceUserPasswordRequired') || 'Los datos no coinciden. Por favor, ingrese su contraseña.');
        errorMessage = errorMsg;
        setIsDeviceUser(false); // Mostrar campo de password
        setPassword(''); // Limpiar password para que el usuario lo ingrese
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = t('login.invalidCredentials') || 'Usuario o contraseña incorrectos';
      } else if (error.message) {
        errorMessage = `${t('login.connectionError') || 'Error de conexión'}: ${error.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 2.5,
        paddingTop: 0,
        paddingBottom: 'calc(2.5 * 8px + 40px)',
        marginTop: '-25vh',
        position: 'relative',
        overflow: 'visible',
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'auto'
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontSize: 32,
          fontWeight: 'bold',
          color: '#fff',
          textAlign: 'center',
          marginBottom: 3
        }}
      >
        {t('login.title')}
      </Typography>
      
      <Box sx={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
        {showSessionTimeoutNotice && (
          <Alert
            severity="info"
            onClose={() => setShowSessionTimeoutNotice(false)}
            sx={{
              marginBottom: 2,
              '& .MuiAlert-message': {
                fontSize: 14,
              },
            }}
          >
            {t('session.previousTimeout')}
          </Alert>
        )}
        {!showSessionTimeoutNotice && loginNotice === 'tokenExpired' && (
          <Alert
            severity="info"
            onClose={() => setLoginNotice(null)}
            sx={{
              marginBottom: 2,
              '& .MuiAlert-message': {
                fontSize: 14,
              },
            }}
          >
            {t('login.tokenExpired')}
          </Alert>
        )}
        {!showSessionTimeoutNotice && loginNotice === 'userDisabled' && (
          <Alert
            severity="warning"
            onClose={() => setLoginNotice(null)}
            sx={{
              marginBottom: 2,
              '& .MuiAlert-message': {
                fontSize: 14,
              },
            }}
          >
            {t('login.userDisabled')}
          </Alert>
        )}
        <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('login.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            sx={{
              marginBottom: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#666'
                },
                '& input': {
                  color: '#000',
                  fontSize: 16,
                  height: 32,
                  padding: '8px 14px'
                }
              }
            }}
          />
          
          {!isDeviceUser && (
            <TextField
              fullWidth
              variant="outlined"
              type="password"
              placeholder={t('login.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              autoComplete="current-password"
              sx={{
                marginBottom: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: '#666'
                  },
                  '& input': {
                    color: '#000',
                    fontSize: 16,
                    height: 32,
                    padding: '8px 14px'
                  }
                }
              }}
            />
          )}
          {isDeviceUser && (
            <Typography
              sx={{
                color: '#2391FF',
                fontSize: 14,
                marginBottom: 2,
                textAlign: 'center',
                padding: 1,
                backgroundColor: 'rgba(35, 145, 255, 0.1)',
                borderRadius: 1
              }}
            >
              {t('login.deviceUserMessage') || 'Dispositivo de Habitacion Compartido'}
            </Typography>
          )}
          
          {error && (
            <Typography
              sx={{
                color: '#ff4444',
                fontSize: 14,
                marginBottom: 1,
                textAlign: 'center',
                padding: 1,
                backgroundColor: 'rgba(255, 68, 68, 0.1)',
                borderRadius: 1
              }}
            >
              {error}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading}
            sx={{
              backgroundColor: loading ? '#666' : '#1976d2',
              height: 40,
              fontSize: 14,
              fontWeight: 'normal',
              color: '#fff',
              textTransform: 'capitalize',
              marginBottom: 1,
              '&:hover': {
                backgroundColor: loading ? '#666' : '#1565c0'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('login.loginButton')}
          </Button>

          <BiometricLogin
            email={email}
            password={password}
            onBiometricSuccess={handleBiometricSuccess}
            onBiometricError={handleBiometricError}
          />
        </form>
      </Box>
        
      {/* Selector de idioma - Posicionado después del formulario con buena separación */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: { xs: 6, sm: 8 }, // Separación duplicada del botón de huella digital
          marginBottom: { xs: 2, sm: 2 }, // Espacio inferior para móviles
          width: '100%'
        }}
      >
        <Box
          component="button"
          onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
          sx={{
            color: '#2391FF',
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            background: 'transparent',
            border: 'none',
            padding: 0,
            '&:hover': {
              color: '#1a7ae6'
            }
          }}
        >
          <Settings sx={{ fontSize: 16, color: 'inherit' }} />
          <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
            {i18n.language === 'es' ? 'Language' : 'Idioma'}
          </Typography>
        </Box>
        
        <Menu
          anchorEl={languageMenuAnchor}
          open={Boolean(languageMenuAnchor)}
          onClose={() => setLanguageMenuAnchor(null)}
          PaperProps={{
            sx: {
              backgroundColor: '#1E1E1E',
              color: '#fff',
              minWidth: 150
            }
          }}
        >
          <MenuItem
            onClick={() => handleLanguageChange('es')}
            selected={i18n.language === 'es'}
            sx={{
              color: i18n.language === 'es' ? '#2391FF' : '#fff',
              '&:hover': {
                backgroundColor: 'rgba(35, 145, 255, 0.1)'
              }
            }}
          >
            {t('login.spanish')}
          </MenuItem>
          <MenuItem
            onClick={() => handleLanguageChange('en')}
            selected={i18n.language === 'en'}
            sx={{
              color: i18n.language === 'en' ? '#2391FF' : '#fff',
              '&:hover': {
                backgroundColor: 'rgba(35, 145, 255, 0.1)'
              }
            }}
          >
            {t('login.english')}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default LoginPage;

